import type { useToast } from "@hightouch/htui";
import { maxBy } from "lodash";
import {
  CopyReferencesIngestionStatus,
  type BrandProfilesListQuery,
  type DestinationsQuery,
} from "src/graphql/types";
import {
  type ContextEntity,
  type CopyExamplesContent,
  isCopyExamplesEntity,
} from "src/pages/context-hub/context-entities/types";
import {
  ALL_PROFILES_ID,
  liveBrandProfileIds,
} from "src/pages/context-hub/context-entities/utils";
import { isRunActionable, isRunPending, type IngestionRun } from "./ingestion";

export type CopyReferencesEntity = ContextEntity & {
  content: CopyExamplesContent;
};

type ToastFn = ReturnType<typeof useToast>["toast"];

export function toastError(
  toast: ToastFn,
  id: string,
  title: string,
  error: unknown,
): void {
  toast({
    id,
    variant: "error",
    title,
    message: error instanceof Error ? error.message : "Unknown error",
  });
}

export const selectBrandProfiles = (data: BrandProfilesListQuery) =>
  data.brand_profiles;

export const selectDestinations = (data: DestinationsQuery) =>
  data.destinations;

export const COPY_REFERENCE_CHANNELS = ["email", "sms", "push", "ads"] as const;
export type CopyReferenceChannel = (typeof COPY_REFERENCE_CHANNELS)[number];

export const CHANNEL_LABELS: Record<CopyReferenceChannel, string> = {
  email: "Email",
  sms: "SMS",
  push: "Push",
  ads: "Ads",
};

export const isCopyReferenceChannel = (
  value: string,
): value is CopyReferenceChannel =>
  COPY_REFERENCE_CHANNELS.includes(value as CopyReferenceChannel);

// All copy-examples configs in a set of context entities, across channels.
export const copyExamplesConfigs = (
  entities: ContextEntity[],
): CopyReferencesEntity[] => entities.filter(isCopyExamplesEntity);

export const configsForChannel = (
  entities: ContextEntity[],
  channel: CopyReferenceChannel,
): CopyReferencesEntity[] =>
  copyExamplesConfigs(entities).filter(
    (entity) => entity.content.channel === channel,
  );

export const runsForChannel = (
  runs: IngestionRun[],
  channel: CopyReferenceChannel,
): IngestionRun[] => runs.filter((run) => run.channel === channel);

export type CopyReferencesScope = {
  channel: CopyReferenceChannel;
  profileId: string | null;
  destinationId: string | null;
};

export const runsForScope = (
  runs: IngestionRun[],
  scope: CopyReferencesScope,
): IngestionRun[] =>
  runs.filter(
    (run) =>
      run.channel === scope.channel &&
      run.brandProfileId === scope.profileId &&
      run.destinationId === scope.destinationId,
  );

export const isGenericScope = (scope: CopyReferencesScope): boolean =>
  scope.profileId == null && scope.destinationId == null;

export const scopeKey = ({
  channel,
  profileId,
  destinationId,
}: CopyReferencesScope): string =>
  `${channel}::${profileId ?? ""}::${destinationId ?? ""}`;

const runScope = (run: IngestionRun): CopyReferencesScope | null =>
  isCopyReferenceChannel(run.channel)
    ? {
        channel: run.channel,
        profileId: run.brandProfileId,
        destinationId: run.destinationId,
      }
    : null;

const configScope = (
  config: CopyReferencesEntity,
): CopyReferencesScope | null =>
  isCopyReferenceChannel(config.content.channel)
    ? {
        channel: config.content.channel,
        profileId: liveBrandProfileIds(config)[0] ?? null,
        destinationId: config.content.target?.destinationId ?? null,
      }
    : null;

// The scopes to render as cards: one generic card per channel (always), plus a
// card for every non-generic tuple with a config or an actionable run. Scoped
// cards are required because card state is an exact-scope match — without them a
// scoped run's progress/review/live set is unreachable. Only *actionable* runs
// (in-flight, ready to review, failed) spawn a card; a consumed run is inert and
// must not keep an empty card alive after its set is deleted.
export const collectCopyReferencesCardScopes = (
  entities: ContextEntity[],
  runs: IngestionRun[],
): CopyReferencesScope[] => {
  const generic: CopyReferencesScope[] = COPY_REFERENCE_CHANNELS.map(
    (channel) => ({ channel, profileId: null, destinationId: null }),
  );

  // Runs come back latest-per-tuple by recency; configs backfill scoped sets
  // that have no recent run.
  const candidates = [
    ...runs.filter(isRunActionable).map(runScope),
    ...copyExamplesConfigs(entities).map(configScope),
  ];

  const seen = new Set(generic.map(scopeKey));
  const scoped: CopyReferencesScope[] = [];
  for (const scope of candidates) {
    if (!scope || isGenericScope(scope)) continue;
    const key = scopeKey(scope);
    if (seen.has(key)) continue;
    seen.add(key);
    scoped.push(scope);
  }
  return [...generic, ...scoped];
};

// The most recently created run drives the run-derived card states.
export const latestRun = (runs: IngestionRun[]): IngestionRun | null =>
  maxBy(runs, (run) => Date.parse(run.createdAt)) ?? null;

// A config with no *live* assignments applies to every profile. Deleted
// assignments are ignored, so an orphaned set reverts to the all-profiles
// convention rather than becoming unreachable.
const configAppliesToAllProfiles = (entity: CopyReferencesEntity): boolean =>
  liveBrandProfileIds(entity).length === 0;

// Whether a config applies to the selected brand profile. An all-profiles config
// (no live assignments) matches every selection; a scoped config matches only
// its exact profile.
export const configAppliesToProfile = (
  entity: CopyReferencesEntity,
  profileId: string,
): boolean => {
  if (configAppliesToAllProfiles(entity)) return true;
  if (profileId === ALL_PROFILES_ID) return false;
  return liveBrandProfileIds(entity).includes(profileId);
};

export const profileIdFromSelection = (selection: string): string | null =>
  selection === ALL_PROFILES_ID ? null : selection;

// Whether a config's stored destination target matches the selected destination.
// A null selection matches only the channel-generic set; legacy rows without a
// target default to channel-generic.
export const configMatchesDestination = (
  entity: CopyReferencesEntity,
  destinationId: string | null,
): boolean => (entity.content.target?.destinationId ?? null) === destinationId;

// The single config that owns a (channel, profile, destination) tuple. Callers
// pass configs already filtered to the channel; this narrows to the destination
// (null = generic) and profile scope, breaking ties by most-recently-updated.
//
// A profile-scoped set takes precedence over the generic (all-profiles) set:
// configAppliesToProfile matches both, so without this a newer generic set would
// shadow the profile's own set and make it unreachable. Fall back to generic only
// when the profile has none of its own.
export const resolveApplicableConfig = (
  configs: CopyReferencesEntity[],
  profileId: string,
  destinationId: string | null,
): CopyReferencesEntity | null => {
  const applicable = configs.filter(
    (config) =>
      configAppliesToProfile(config, profileId) &&
      configMatchesDestination(config, destinationId),
  );
  if (applicable.length === 0) return null;
  // Use the live-assignment helper (not appliesToAllBrandProfiles, which counts
  // soft-deleted rows) so the partition matches configAppliesToProfile — else a
  // deleted-only config would be miscategorized as profile-scoped and shadow the
  // real generic set.
  const profileScoped = applicable.filter(
    (config) => !configAppliesToAllProfiles(config),
  );
  const preferred = profileScoped.length > 0 ? profileScoped : applicable;
  return maxBy(preferred, (config) => Date.parse(config.updatedAt)) ?? null;
};

// The count of active (non-excluded) examples — the set the agent draws on.
export const countActiveExamples = (
  examples: readonly { excluded: boolean }[],
): number => examples.filter((example) => !example.excluded).length;

// The live-set details for a tuple. Shared by the `live` and `failed` states so
// a live set stays reachable when the latest run failed.
export type LiveCardDetails = {
  resolvedConfig: CopyReferencesEntity;
  exampleCount: number;
  lastUpdatedAt: string;
};

export type ChannelCardState =
  | { kind: "fetching"; run: IngestionRun }
  | { kind: "readyToReview"; run: IngestionRun }
  | {
      kind: "failed";
      run: IngestionRun;
      // The live set for this profile, if one exists, so "View references"
      // stays reachable even though the latest run failed.
      live: LiveCardDetails | null;
    }
  | ({ kind: "live" } & LiveCardDetails)
  | { kind: "notConfigured" };

// The applicable live set for a (channel, profile, destination) tuple, or null
// when none.
export const resolveLiveCardDetails = (
  channelConfigs: CopyReferencesEntity[],
  profileId: string,
  destinationId: string | null,
): LiveCardDetails | null => {
  const resolvedConfig = resolveApplicableConfig(
    channelConfigs.filter((config) => config.isActive),
    profileId,
    destinationId,
  );
  if (!resolvedConfig) return null;
  return {
    resolvedConfig,
    exampleCount: countActiveExamples(resolvedConfig.content.examples),
    lastUpdatedAt: resolvedConfig.updatedAt,
  };
};

// Derive a channel card's state for the selected profile, in precedence order:
// fetching > readyToReview > failed > live > notConfigured. A complete run whose
// review is consumed falls through to live/notConfigured (the entity is the
// source of truth). The failed state carries the live set when one exists, so
// "View references" stays reachable after a later refresh fails.
export const deriveChannelCardState = (
  channelConfigs: CopyReferencesEntity[],
  channelRuns: IngestionRun[],
  profileId: string,
  destinationId: string | null,
): ChannelCardState => {
  const live = resolveLiveCardDetails(channelConfigs, profileId, destinationId);
  const selectedProfileId = profileIdFromSelection(profileId);
  const run = latestRun(
    channelRuns.filter(
      (candidate) =>
        candidate.brandProfileId === selectedProfileId &&
        candidate.destinationId === destinationId,
    ),
  );
  if (run) {
    if (isRunPending(run)) return { kind: "fetching", run };
    if (run.reviewId && run.reviewPending) {
      return { kind: "readyToReview", run };
    }
    if (
      run.status === CopyReferencesIngestionStatus.Failed ||
      run.status === CopyReferencesIngestionStatus.Canceled
    ) {
      return { kind: "failed", run, live };
    }
  }

  if (live) {
    return { kind: "live", ...live };
  }

  return { kind: "notConfigured" };
};
